import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent } from '../../components/ui';
import { SplitViewTabBar } from '../../components/generic/SplitViewWithTabs';
import ComplaintDetailsTab from './components/tabs/ComplaintDetailsTab';
import ComplaintTimelineTab from './components/tabs/ComplaintTimelineTab';
import ComplaintAuditLogTab from './components/tabs/ComplaintAuditLogTab';
import { useFlexibleList } from '../../hooks/useSWR';
import { DocumentTextIcon, ClipboardCheckIcon, ClockIcon, XIcon } from '../../components/ui/Icons';

/**
 * Custom Split View Panel for Complaint module with responsive tab carousel
 * 
 * Features:
 * - Smart Detection: Automatically calculates how many tabs can fit based on drawer width
 * - Config-Based: Uses actual drawerWidth from module config for precise calculations
 * - Clean Navigation: Only shows Previous/Next arrows when needed
 * - No Overflow: All elements stay within panel boundaries
 * - Responsive: Adapts to any drawer width configuration
 * 
 * Tabs:
 * - Details: Complaint information, actions taken, corrective/preventive actions, and file attachments
 * - Timeline: Complaint activity timeline
 * - Audit Log: Complete audit trail for the complaint
 */
const CustomComplaintSplitView = ({ 
  isOpen, 
  selectedRecord, 
  selectedRecordFiles, 
  config, 
  fields, 
  labels,
  onClose,
  onRecordUpdate // Callback to refresh Complaint data
}) => {
  const { theme } = useTheme();

  // Fetch Complaint actions count for the selected record
  const { data: complaintActions = [] } = useFlexibleList(
    selectedRecord?.id ? `/complaint-action/complaint/${selectedRecord.id}` : null
  );
  
  // Fetch Complaint actions taken count for the selected record
  const { data: complaintActionsTaken = [] } = useFlexibleList(
    selectedRecord?.id ? `/complaint-action-taken/complaint/${selectedRecord.id}` : null
  );
  
  // Fetch file attachments count (if the endpoint exists)
  // Filter out deleted files
  const { data: allFileAttachments = [] } = useFlexibleList(
    selectedRecord?.id ? `/file-attachment/entity/${config?.entityTypeId || 7}/${selectedRecord.id}` : null
  );
  const fileAttachments = allFileAttachments.filter(file => !file.isDeleted);
  
  // Fetch timeline entries count for the selected record
  const { data: timelineEntries = [] } = useFlexibleList(
    selectedRecord?.id ? `/complaint-timeline/by-complaint/${selectedRecord.id}` : null
  );
  
  // Fetch Complaint audit logs using master entity endpoint (includes all related entities)
  const { data: auditLogs = [] } = useFlexibleList(
    selectedRecord?.id ? `/audit-log/master/${config?.features?.masterEntityTypeId || 7}/${selectedRecord.id}` : null
  );
  
  // Calculate total actions count (actions taken + corrective/preventive actions)
  const totalActionsCount = complaintActionsTaken.length + complaintActions.length;
  
  // Define all tabs with their data
  const allTabs = [
    {
      value: "details",
      label: "Details",
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge: totalActionsCount > 0 ? totalActionsCount : fileAttachments.length,
      badgeColor: totalActionsCount > 0 ? "bg-blue-600" : "bg-green-600"
    },
    {
      value: "timeline",
      label: "Timeline",
      icon: <ClockIcon className="w-4 h-4" />,
      badge: timelineEntries.length,
      badgeColor: "bg-orange-600"
    },
    {
      value: "audit-log",
      label: "Audit Log",
      icon: <ClipboardCheckIcon className="w-4 h-4" />,
      badge: auditLogs.length,
      badgeColor: "bg-red-600"
    }
  ];
  
  if (!isOpen) return null;

  return (
    <div 
      className={cn("hidden lg:block min-h-0 h-full pb-2")}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <div className={cn("min-h-0 h-full rounded-xl border flex flex-col", theme.border.light, theme.colors.surface, theme.shadow.md)}>
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className={cn("text-sm font-semibold", theme.text.primary)}>
            {selectedRecord ? `Complaint - ${selectedRecord.code || selectedRecord.id}` : "Select a complaint to view details"}
          </div>
          <button
            onClick={onClose}
            className={cn("p-1 rounded hover:opacity-80", theme.text.secondary)}
            title={labels.closeText || 'Close'}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Content */}
        {selectedRecord ? (
          <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0 px-3 py-2">
            <SplitViewTabBar tabs={allTabs} />
            
            <div className="flex-1 flex flex-col min-h-0">
              {/* Details Tab */}
              <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
                <ComplaintDetailsTab 
                  selectedRecord={selectedRecord}
                  selectedRecordFiles={selectedRecordFiles}
                  config={config}
                  fields={fields}
                  onRecordUpdate={onRecordUpdate}
                />
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline" className="mt-0 flex-1 flex flex-col min-h-0">
                <ComplaintTimelineTab 
                  selectedRecord={selectedRecord}
                />
              </TabsContent>

              {/* Audit Log Tab */}
              <TabsContent value="audit-log" className="mt-0 flex-1 flex flex-col min-h-0">
                <ComplaintAuditLogTab 
                  selectedRecord={selectedRecord}
                />
              </TabsContent>
            </div>
          </Tabs>
        ) : (
          <div className={cn("p-4 text-sm", theme.text.secondary)}>
            {labels.noSelectionText || "No complaint selected"}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomComplaintSplitView;
